import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Trash2, Plus, X, CalendarPlus } from "lucide-react";

const DAYS = [
  { k: "mon", label: "Dilluns" }, { k: "tue", label: "Dimarts" }, { k: "wed", label: "Dimecres" },
  { k: "thu", label: "Dijous" }, { k: "fri", label: "Divendres" }, { k: "sat", label: "Dissabte" },
  { k: "sun", label: "Diumenge" },
];

const DEFAULT_SLOT = { open: "09:00", close: "20:00" };

export default function HoursEditor({ hours, holidays, onChange }) {
  const [applyAllOpen, setApplyAllOpen] = useState(false);
  const h = hours || {};
  const hol = holidays || [];

  const setDay = (day, slots) => onChange({ hours: { ...h, [day]: slots }, holidays: hol });
  const addSlot = (day) => setDay(day, [...(h[day] || []), { ...DEFAULT_SLOT }]);
  const removeSlot = (day, idx) => setDay(day, (h[day] || []).filter((_, i) => i !== idx));
  const updateSlot = (day, idx, patch) => {
    const next = (h[day] || []).map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setDay(day, next);
  };
  const closeDay = (day) => setDay(day, []);
  const copyToAll = (fromDay) => {
    const src = h[fromDay] || [];
    const next = {};
    for (const d of DAYS) next[d.k] = src.map((s) => ({ ...s }));
    onChange({ hours: next, holidays: hol });
  };

  const addHoliday = () => onChange({ hours: h, holidays: [...hol, { date: "", label: "", closed: true }] });
  const updateHoliday = (i, patch) => onChange({ hours: h, holidays: hol.map((x, j) => (i === j ? { ...x, ...patch } : x)) });
  const removeHoliday = (i) => onChange({ hours: h, holidays: hol.filter((_, j) => j !== i) });

  return (
    <div className="space-y-6" data-testid="hours-editor">
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label>Horari setmanal</Label>
          <div className="text-xs text-muted-foreground">Deixa buit un dia per marcar-lo com "Tancat"</div>
        </div>
        <div className="space-y-2">
          {DAYS.map(({ k, label }) => {
            const slots = h[k] || [];
            return (
              <Card key={k} className="rounded-xl border" data-testid={`hours-day-${k}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="w-24 text-sm font-medium">{label}</div>
                    <div className="flex flex-wrap gap-2 flex-1">
                      {slots.length === 0 && <span className="text-xs text-muted-foreground italic">Tancat</span>}
                      {slots.map((s, i) => (
                        <div key={i} className="flex items-center gap-1 bg-secondary rounded-full px-2 py-1">
                          <Input type="time" value={s.open} onChange={(e) => updateSlot(k, i, { open: e.target.value })} className="h-7 w-24 border-0 bg-transparent px-1 focus-visible:ring-0" data-testid={`hours-${k}-slot-${i}-open`} />
                          <span className="text-xs text-muted-foreground">→</span>
                          <Input type="time" value={s.close} onChange={(e) => updateSlot(k, i, { close: e.target.value })} className="h-7 w-24 border-0 bg-transparent px-1 focus-visible:ring-0" data-testid={`hours-${k}-slot-${i}-close`} />
                          <button type="button" onClick={() => removeSlot(k, i)} className="w-6 h-6 rounded-full hover:bg-white flex items-center justify-center" data-testid={`hours-${k}-slot-${i}-remove`}><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-1">
                      <Button type="button" size="sm" variant="ghost" onClick={() => addSlot(k)} className="rounded-full" data-testid={`hours-${k}-add`}><Plus className="w-3.5 h-3.5 mr-1" /> Franja</Button>
                      {slots.length > 0 && <Button type="button" size="sm" variant="ghost" onClick={() => copyToAll(k)} className="rounded-full text-xs">Aplicar a tots</Button>}
                      {slots.length > 0 && <Button type="button" size="sm" variant="ghost" onClick={() => closeDay(k)} className="rounded-full text-xs text-destructive">Tancat</Button>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <Label>Festius i dies especials</Label>
          <Button type="button" size="sm" variant="outline" onClick={addHoliday} className="rounded-full" data-testid="holiday-add-btn"><CalendarPlus className="w-3.5 h-3.5 mr-1" /> Afegir</Button>
        </div>
        <div className="space-y-2">
          {hol.map((x, i) => (
            <div key={i} className="grid grid-cols-[140px_1fr_120px_40px] gap-2 items-center" data-testid={`holiday-row-${i}`}>
              <Input type="date" value={x.date || ""} onChange={(e) => updateHoliday(i, { date: e.target.value })} className="rounded-lg" />
              <Input placeholder="Motiu (p.ex. Nadal)" value={x.label || ""} onChange={(e) => updateHoliday(i, { label: e.target.value })} className="rounded-lg" />
              <select value={x.closed ? "closed" : "special"} onChange={(e) => updateHoliday(i, { closed: e.target.value === "closed" })} className="rounded-lg border px-3 py-2 text-sm bg-background">
                <option value="closed">Tancat</option>
                <option value="special">Horari especial</option>
              </select>
              <Button type="button" size="icon" variant="ghost" onClick={() => removeHoliday(i)} data-testid={`holiday-remove-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
          {hol.length === 0 && <div className="text-xs text-muted-foreground italic">Sense dies especials.</div>}
        </div>
      </div>
    </div>
  );
}
